import { HttpResponse } from "@angular/common/http";
import { Component, Inject, OnInit } from "@angular/core";
import { AbstractControl, FormControl, FormGroup, Validators } from "@angular/forms";
import {
	MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
	MatLegacyDialog as MatDialog,
	MatLegacyDialogRef as MatDialogRef,
} from "@angular/material/legacy-dialog";
import { extractFilenameFromExportHeaders } from "@app/_helpers/extract-filename-from-content-disposition";
import { ObserveFormGroupErrors, useFormErrorObservable } from "@app/_helpers/get-error-observable";
import { canAccessProjectSummaryExport, isAdmin } from "@app/_helpers/permission";
import { ExportProgress } from "@app/_helpers/progress-interface";
import { createRxGroupWithCache } from "@app/_helpers/rx-value";
import { useUserSettings } from "@app/_helpers/useUtils";
import { createRxValue, distinctUntilChangedJson, fromRxValue } from "@app/_helpers/utils";
import { AppService } from "@app/app.service";
import { ExportProjectHoursSchema, exportProjectHoursSchema } from "@app/lib/export/projecthours";
import { ExportFilters, ReportsService } from "@app/pages/reports-page/reports.service";
import RoundingConfig from "@app/shared/dialogs/rounding-dialog/models/rounding-config";
import RoundingConfigData from "@app/shared/dialogs/rounding-dialog/models/rounding-config-data";
import { RoundingDialogComponent, RoundingTypes } from "@app/shared/dialogs/rounding-dialog/rounding-dialog.component";
import { environment } from "@env/environment";
import { IsBetaWindow } from "@env/version";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { TranslateService } from "@ngx-translate/core";
import { ApplicationSettingsQuery, Logger, TimesService, UserSettingsQuery } from "@timeghost/web-api";
import { saveAs } from "file-saver";
import { combineLatest, distinctUntilChanged, firstValueFrom, map, startWith } from "rxjs";
export const TIMEZONE_TYPES = [
	{ value: "keep", name: "utils.timezone-keep" },
	{ value: "adjust", name: "utils.timezone-adjust" },
];
type ReportExportFilter = Omit<ExportFilters, "startDate" | "endDate"> & { startDate: Date; endDate: Date };
const PROJECT_EXPORT_COLUMNS = {
	client: true,
	project: true,
	externalId: true,
	projectManagers: true,
	projectDescription: true,
	task: true,
	description: true,
	user: true,
	staffNumber: true,
	tags: true,
	projectTags: true,
	billable: true,
	date: true,
	start: true,
	end: true,
	timeZone: true,
	sumInHours: true,
	sumInDays: true,
	earnings: true,
	costs: true,
	balance: true,
	summary: true,
	projectValues: true,
	projectAmounts: true,
	projectStatus: true,
};
const SUMMARY_EXPORT_COLUMNS = {
	project: true,
	projectTags: true,
	externalId: true,
	projectManagers: true,
	projectBudgetHours: true,
	projectBudgetDays: true,
	totalBillableHours: true,
	totalBillableDays: true,
	notBillableTotalHours: true,
	notBillableTotalDays: true,
	totalTimeHours: true,
	totalTimeDays: true,
	remainingBudgetHours: true,
	remainingBudgetDays: true,
	budgetUsedPercent: true,
	completedStatus: true,
} as const;
type ProjecthoursExportColumn = keyof typeof PROJECT_EXPORT_COLUMNS;
type SummaryExportColumn = keyof typeof SUMMARY_EXPORT_COLUMNS;
type ExportColumn = ProjecthoursExportColumn | SummaryExportColumn;
type ExportColumnSet = "projecthours" | "summary";
type ExportFileType = "excel" | "csv" | "pdf";
type ApiTimezone = "keep" | "local";
type ExportColumnGroup = { nameKey: string; columns: ExportColumn[] };
type ExportColumnGroupDefinition = Readonly<{ nameKey: string; columns: ReadonlyArray<ExportColumn> }>;
type ProjectSummaryUserMetric = "billable_hours" | "income" | "all_times" | "non_billable_hours";
const PROJECT_SUMMARY_USER_METRICS: ReadonlyArray<ProjectSummaryUserMetric> = [
	"all_times",
	"billable_hours",
	"non_billable_hours",
	"income",
];
const PROJECT_AGGREGATION_COLUMNS: ExportColumn[] = ["projectStatus", "projectValues", "projectAmounts"];
export const PROJECT_EXPORT_COLUMNS_ARR = Object.keys(PROJECT_EXPORT_COLUMNS) as ProjecthoursExportColumn[];
export const SUMMARY_EXPORT_COLUMNS_ARR = Object.keys(SUMMARY_EXPORT_COLUMNS) as SummaryExportColumn[];
const EXPORT_COLUMNS_BY_SET = {
	projecthours: PROJECT_EXPORT_COLUMNS_ARR,
	summary: SUMMARY_EXPORT_COLUMNS_ARR,
} as const;
const ALL_EXPORT_COLUMNS_ARR = Array.from(
	new Set([...PROJECT_EXPORT_COLUMNS_ARR, ...SUMMARY_EXPORT_COLUMNS_ARR])
) as ExportColumn[];
const PROJECTHOURS_EXPORT_COLUMN_GROUPS: ReadonlyArray<ExportColumnGroupDefinition> = [
	{
		nameKey: "reports.export-columns.groups.project",
		columns: ["client", "project", "externalId", "projectManagers", "projectDescription", "task", "description"],
	},
	{ nameKey: "reports.export-columns.groups.user", columns: ["user", "staffNumber"] },
	{
		nameKey: "reports.export-columns.groups.time",
		columns: ["date", "start", "end", "timeZone", "sumInHours", "sumInDays"],
	},
	{ nameKey: "reports.export-columns.groups.labels", columns: ["tags", "projectTags"] },
	{
		nameKey: "reports.export-columns.groups.financial",
		columns: ["billable", "earnings", "costs", "balance", "summary"],
	},
	{
		nameKey: "reports.export-columns.groups.projectAggregations",
		columns: ["projectValues", "projectAmounts", "projectStatus"],
	},
];
const SUMMARY_EXPORT_COLUMN_GROUPS: ReadonlyArray<ExportColumnGroupDefinition> = [
	{
		nameKey: "reports.export-columns.groups.project",
		columns: ["project", "externalId", "projectManagers"],
	},
	{
		nameKey: "reports.export-columns.groups.labels",
		columns: ["projectTags"],
	},
	{
		nameKey: "reports.export-columns.groups.projectSummary",
		columns: [
			"projectBudgetHours",
			"projectBudgetDays",
			"totalBillableHours",
			"totalBillableDays",
			"notBillableTotalHours",
			"notBillableTotalDays",
			"totalTimeHours",
			"totalTimeDays",
			"remainingBudgetHours",
			"remainingBudgetDays",
			"budgetUsedPercent",
			"completedStatus",
		],
	},
];
const EXPORT_COLUMN_GROUPS_BY_SET: Readonly<Record<ExportColumnSet, ReadonlyArray<ExportColumnGroupDefinition>>> = {
	projecthours: PROJECTHOURS_EXPORT_COLUMN_GROUPS,
	summary: SUMMARY_EXPORT_COLUMN_GROUPS,
};
const buildOrderedExportColumns = (groups: ReadonlyArray<ExportColumnGroupDefinition>): ReadonlyArray<ExportColumn> =>
	Array.from(new Set(groups.flatMap((group) => group.columns))) as ExportColumn[];
const ORDERED_EXPORT_COLUMNS_BY_SET: Readonly<Record<ExportColumnSet, ReadonlyArray<ExportColumn>>> = {
	projecthours: buildOrderedExportColumns(PROJECTHOURS_EXPORT_COLUMN_GROUPS),
	summary: buildOrderedExportColumns(SUMMARY_EXPORT_COLUMN_GROUPS),
};
type ExportTypeHeader = { name: string; type: "header"; csv?: false; pdf?: false };
type ExportTypeOption = {
	name: string;
	type?: undefined;
	groupedByProject: boolean;
	view: ExportProjectHoursSchema["view"];
	disabledColumns?: false | true | ExportColumn[];
	columnSet?: ExportColumnSet;
	csv?: boolean;
	pdf?: boolean;
	beta?: boolean;
	ext?: string;
};
type ExportType = ExportTypeHeader | ExportTypeOption;
interface ReportsExportsConfig {
	columns?: ExportColumn[];
	projecthoursColumns?: ProjecthoursExportColumn[];
	summaryColumns?: SummaryExportColumn[];
}
interface ReportsExportFormValue {
	columns: Record<ExportColumn, boolean>;
	columnType: 0 | 1;
	exportType: string;
	fileType: ExportFileType;
	projectSummaryUserMetric: ProjectSummaryUserMetric;
	projectSummaryUserProjectAlias: "name" | "externalId" | "id";
	rounding: RoundingConfigData;
	timezone: string;
}
const log = new Logger("ReportsExportDialogComponent");
@ObserveFormGroupErrors()
@UntilDestroy()
@Component({
	selector: "tg-reports-export-dialog",
	templateUrl: "./reports-export-dialog.component.html",
	styleUrls: ["./reports-export-dialog.component.scss"],
})
export class ReportsExportDialogComponent implements OnInit {
	readonly userSettings = useUserSettings();
	readonly exportTypes = createRxValue(
		(
			[
				{ name: "Excel", type: "header" },
				{
					name: "reports.exports.excel-download-grouped-user",
					groupedByProject: false,
					view: "grouped_by_user",
					disabledColumns: [...PROJECT_AGGREGATION_COLUMNS, "user", "staffNumber"],
					columnSet: "projecthours",
				},
				{
					name: "reports.exports.excel-download-grouped",
					groupedByProject: true,
					view: "grouped_by_project",
					columnSet: "projecthours",
					disabledColumns: ["projectDescription", "project", "projectTags", "client"],
				},
				{
					name: "reports.exports.excel-download-simple",
					groupedByProject: false,
					view: "grouped_by_workspace",
					disabledColumns: PROJECT_AGGREGATION_COLUMNS,
					columnSet: "projecthours",
				},
				{
					name: "reports.exports.excel-download-project_summary",
					groupedByProject: false,
					view: "project_summary",
					disabledColumns: false,
					columnSet: "summary",
				},
				{
					name: "reports.exports.excel-download-project_summary_user",
					groupedByProject: false,
					view: "project_summary_user",
					disabledColumns: true,
				},
				{ name: "CSV", type: "header" },
				{
					name: "reports.exports.csv-file-project_summary",
					groupedByProject: false,
					csv: true,
					view: "project_summary",
					disabledColumns: false,
					columnSet: "summary",
				},
				{
					name: "reports.exports.csv-file-project_summary_user",
					groupedByProject: false,
					csv: true,
					view: "project_summary_user",
					disabledColumns: true,
				},
				{
					name: "reports.exports.csv-file",
					groupedByProject: false,
					csv: true,
					view: "simple_list",
					disabledColumns: PROJECT_AGGREGATION_COLUMNS,
					columnSet: "projecthours",
				},
				{ name: "PDF", type: "header" },
				{
					name: "reports.exports.pdf-file",
					groupedByProject: false,
					pdf: true,
					view: "grouped_by_client",
					disabledColumns: true,
				},
			] as ExportType[]
		).filter((x) => this.isVisibleExportType(x))
	);
	private get availableExportTypes(): ExportType[] {
		return this.exportTypes.value as ExportType[];
	}
	readonly fileTypeOptions: ReadonlyArray<{
		value: ExportFileType;
		title: string;
		description: string;
		icon: string;
	}> = [
		{ value: "excel", title: "Excel", description: ".xlsx", icon: "table_view" },
		{ value: "csv", title: "CSV", description: ".csv", icon: "description" },
		{ value: "pdf", title: "PDF", description: ".pdf", icon: "picture_as_pdf" },
	];
	get visibleFileTypeOptions() {
		return this.fileTypeOptions.filter((item) => this.hasExportTypeForFileType(item.value));
	}
	get visibleExportTypeOptionsForSelectedFileType(): ExportTypeOption[] {
		return this.getExportTypeOptionsByFileType(this.formValue.fileType);
	}
	get showExportTypeSelector(): boolean {
		return this.visibleExportTypeOptionsForSelectedFileType.length > 1;
	}
	get shouldShowPdfExportHint(): boolean {
		return this.formValue.fileType === "pdf";
	}
	get pdfExportHintKey(): string {
		const hasMultipleSelectedCustomers = (this.data?.filter?.clients?.length ?? 0) > 1;
		return hasMultipleSelectedCustomers
			? "reports.exports.pdf-zip-file-description"
			: "reports.exports.pdf-file-description";
	}
	readonly timezoneTypes = TIMEZONE_TYPES;
	readonly projectSummaryUserMetricOptions: ReadonlyArray<{ value: ProjectSummaryUserMetric; name: string }> =
		PROJECT_SUMMARY_USER_METRICS.map((metric) => ({
			value: metric,
			name: `reports.exports.projectSummaryUserMetricOptions.${metric}`,
		}));
	readonly projectSummaryUserProjectAliasOptions: ReadonlyArray<{
		value: "name" | "externalId" | "id";
		name: string;
	}> = [
		{ value: "name", name: "Name" },
		{ value: "externalId", name: "External ID" },
		{ value: "id", name: "ID" },
	];
	readonly group = createRxGroupWithCache(
		new FormGroup({
			projectSummaryUserMetric: new FormControl<ProjectSummaryUserMetric>("billable_hours", [
				Validators.required,
				(ctrl) => (ctrl.value && PROJECT_SUMMARY_USER_METRICS.includes(ctrl.value) ? null : { required: true }),
			]),
			projectSummaryUserProjectAlias: new FormControl<"name" | "externalId" | "id">("name", [
				Validators.required,
				(ctrl) =>
					ctrl.value
						? !this.projectSummaryUserProjectAliasOptions.find((x) => x.value === ctrl.value)
							? { required: true }
							: null
						: null,
			]),
			timezone: new FormControl("keep", [
				Validators.required,
				(ctrl) =>
					ctrl.value
						? !this.timezoneTypes?.find((x) => x.value === ctrl.value)
							? { required: true }
							: null
						: null,
			]),
			fileType: new FormControl<ExportFileType>("excel", [Validators.required]),
			exportType: new FormControl("reports.exports.excel-download-grouped", [Validators.required]),
			rounding: new FormControl<RoundingConfigData>(
				new RoundingConfigData(
					this.data?.filter?.rounding?.enabled
						? ((rounding) => ({
								enabled: rounding.enabled,
								minutes: rounding.minutes,
								type: Object.entries(RoundingTypes).find(
									([key]) => key.toLowerCase() === (rounding.type as any as string)
								)?.[1] as RoundingTypes,
							}))(this.data.filter.rounding)
						: {
								enabled: false,
								minutes: 15,
								type: RoundingTypes.UpTo,
							}
				)
			),
			columnType: new FormControl<0 | 1>(0),
			columns: new FormGroup({
				...ALL_EXPORT_COLUMNS_ARR.reduce(
					(acc, r) => {
						acc[r] = new FormControl<boolean>(true);
						return acc;
					},
					{} as { [key: string]: AbstractControl }
				),
			}),
		}),
		"reports.exportConfig"
	);
	get formValue(): ReportsExportFormValue {
		return this.group.getRawValue() as ReportsExportFormValue;
	}
	readonly formValues$ = fromRxValue(
		this.group.valueChanges.pipe(
			startWith(this.formValue),
			map(() => this.formValue)
		),
		this.formValue
	);
	readonly selectedExportType$ = fromRxValue(
		this.formValues$.asObservable().pipe(map((x) => this.getExportTypeByName(x.exportType))),
		this.getExportTypeByName(this.formValue.exportType)
	);
	readonly filteredExportTypes$ = fromRxValue(
		this.formValues$.asObservable().pipe(map((x) => this.getExportTypeOptionsByFileType(x.fileType))),
		this.getExportTypeOptionsByFileType(this.formValue.fileType)
	);
	isLoading: boolean;
	private canUseBetaExport(): boolean {
		if (environment.production && !IsBetaWindow()) {
			return false;
		}
		const userSettings = this.userSettings;
		if (!userSettings?.workspace) {
			return false;
		}
		if (isAdmin(userSettings)) {
			return true;
		}
		return userSettings.workspace.settings?.nonAdminsCanAccessBeta !== false;
	}
	private isExportTypeHeader(exportType: ExportType): exportType is ExportTypeHeader {
		return exportType.type === "header";
	}
	private isProjectSummaryView(view: ExportProjectHoursSchema["view"]): boolean {
		return view === "project_summary" || view === "project_summary_user";
	}
	private isVisibleExportType(exportType: ExportType): boolean {
		if (this.isExportTypeHeader(exportType)) {
			return true;
		}
		if (exportType.beta) {
			return this.canUseBetaExport();
		}
		if (this.isProjectSummaryView(exportType.view) && !canAccessProjectSummaryExport(this.userSettings)) {
			return false;
		}
		return true;
	}
	private getExportTypeOptionsByFileType(fileType: ExportFileType): ExportTypeOption[] {
		return this.availableExportTypes.filter((item): item is ExportTypeOption => {
			if ((item as ExportTypeHeader).type) return false;
			const option = item as ExportTypeOption;
			if (fileType === "pdf") {
				return !!option.pdf;
			}
			if (option.pdf) return false;
			return fileType === "csv" ? !!option.csv : !option.csv;
		});
	}
	private hasExportTypeForFileType(fileType: ExportFileType): boolean {
		return this.getExportTypeOptionsByFileType(fileType).length > 0;
	}
	private getValidExportTypeNameByFileType(fileType: ExportFileType, currentExportTypeName: string): string {
		const selected = this.getExportTypeByName(currentExportTypeName);
		if (selected) {
			const isPdf = !!selected.pdf;
			const isCsv = !!selected.csv;
			const isExcel = !isPdf && !isCsv;
			if ((fileType === "pdf" && isPdf) || (fileType === "csv" && isCsv) || (fileType === "excel" && isExcel)) {
				return currentExportTypeName;
			}
		}
		const fallback = this.getExportTypeOptionsByFileType(fileType)[0];
		return fallback?.name ?? currentExportTypeName;
	}
	private getExportTypeByName(name: string): ExportTypeOption | null {
		const exportType = this.availableExportTypes.find((x) => x.name === name);
		if (!exportType || (exportType as ExportTypeHeader).type === "header") {
			return null;
		}
		return exportType as ExportTypeOption;
	}
	private getConfiguredColumnsBySet(columnSet: ExportColumnSet): ExportColumn[] | null {
		const config = this.config;
		if (columnSet === "projecthours" && config?.projecthoursColumns?.length) {
			return config.projecthoursColumns;
		}
		if (columnSet === "summary" && config?.summaryColumns?.length) {
			return config.summaryColumns;
		}
		if (config?.columns?.length) {
			return config.columns;
		}
		return null;
	}
	private setActiveColumns(columns: ExportColumn[], selectedColumns?: ExportColumn[] | null) {
		const selected = new Set((selectedColumns?.length ? selectedColumns : columns) as ExportColumn[]);
		const allowed = new Set(columns);
		const columnState = ALL_EXPORT_COLUMNS_ARR.reduce(
			(acc, column) => {
				acc[column] = allowed.has(column) && selected.has(column);
				return acc;
			},
			{} as Record<ExportColumn, boolean>
		);
		this.group.patchValue(
			{
				columns: columnState,
			},
			{ emitEvent: true }
		);
	}
	private includesCompletedProjects(filter: Pick<ReportExportFilter, "projectStatus"> = this.data?.filter): boolean {
		return filter?.projectStatus !== "not_completed";
	}
	private resolveExportColumns(exportType: ExportTypeOption | null): ExportColumn[] {
		if (!exportType?.columnSet) {
			return [];
		}
		const defaultColumns = EXPORT_COLUMNS_BY_SET[exportType.columnSet] as ExportColumn[];
		const disabledColumns = exportType.disabledColumns ?? false;
		if (disabledColumns === true) return [];
		const filteredColumns =
			disabledColumns === false
				? [...defaultColumns]
				: defaultColumns.filter((x) => !disabledColumns.includes(x));
		const enabledColumns =
			exportType.columnSet === "summary" && !this.includesCompletedProjects()
				? filteredColumns.filter((column) => column !== "completedStatus")
				: filteredColumns;
		const enabledSet = new Set(enabledColumns);
		const orderedColumns = ORDERED_EXPORT_COLUMNS_BY_SET[exportType.columnSet] as ExportColumn[];
		const sortedColumns = orderedColumns.filter((column) => enabledSet.has(column));
		if (sortedColumns.length === enabledColumns.length) {
			return sortedColumns;
		}
		const sortedSet = new Set(sortedColumns);
		return [...sortedColumns, ...enabledColumns.filter((column) => !sortedSet.has(column))];
	}
	private updateVisibleColumnsSelection(isSelected: boolean) {
		const visibleColumns = new Set(this.exportColumns.value);
		const currentColumns = this.group.getRawValue().columns;
		const nextColumns = ALL_EXPORT_COLUMNS_ARR.reduce(
			(acc, column) => {
				acc[column] = visibleColumns.has(column) ? isSelected : currentColumns[column];
				return acc;
			},
			{} as Record<ExportColumn, boolean>
		);
		this.group.patchValue({ columns: nextColumns });
	}
	selectAllColumns() {
		this.updateVisibleColumnsSelection(true);
	}
	clearSelectedColumns() {
		this.updateVisibleColumnsSelection(false);
	}
	resetDefaultColumnSelection() {
		const exportType = this.getSelectedExportType(this.formValue.exportType);
		const defaultColumns = this.resolveExportColumns(exportType);
		this.setActiveColumns(this.exportColumns.value, defaultColumns);
	}
	private selectAllValidColumnsForExportType(exportType: ExportTypeOption | null) {
		const activeColumns = this.resolveExportColumns(exportType);
		this.setActiveColumns(activeColumns, activeColumns);
	}
	private applyColumnsForExportType(
		exportType: ExportTypeOption | null,
		setCustomWhenConfigured = false,
		resetSelection = false
	) {
		const activeColumns = this.resolveExportColumns(exportType);
		const configuredColumns =
			!resetSelection && exportType?.columnSet ? this.getConfiguredColumnsBySet(exportType.columnSet) : null;
		const selectedColumns = configuredColumns
			? activeColumns.filter((column) => configuredColumns.includes(column))
			: activeColumns;
		this.setActiveColumns(activeColumns, selectedColumns);
		if (resetSelection) {
			this.group.patchValue({ columnType: 0 }, { emitEvent: false });
			return;
		}
		if (setCustomWhenConfigured && configuredColumns?.length) {
			this.group.patchValue({ columnType: 1 }, { emitEvent: false });
		}
	}
	readonly exportColumns = fromRxValue(
		combineLatest([this.formValues$.asObservable(), this.selectedExportType$.asObservable()]).pipe(
			map(([values, exportType]) => {
				return this.resolveExportColumns(exportType);
			})
		),
		[] as ExportColumn[]
	);
	readonly hasColumns$ = this.exportColumns.asObservable().pipe(map((x) => x.length > 0));
	readonly groupedExportColumns$ = fromRxValue(
		combineLatest([this.exportColumns.asObservable(), this.selectedExportType$.asObservable()]).pipe(
			map(([columns, exportType]) => {
				if (!exportType?.columnSet) {
					return [];
				}
				const visibleColumns = new Set(columns);
				return EXPORT_COLUMN_GROUPS_BY_SET[exportType.columnSet]
					.map((group) => ({
						nameKey: group.nameKey,
						columns: group.columns.filter((column) => visibleColumns.has(column)),
					}))
					.filter((group) => group.columns.length > 0);
			})
		),
		[] as ExportColumnGroup[]
	);
	private getFilteredActiveColumns(values: ReportsExportFormValue = this.formValue): ExportColumn[] {
		const exportColumns = this.exportColumns.value;
		return [...exportColumns.filter((column) => values.columns[column])];
	}
	readonly selectedExportColumns$ = fromRxValue(
		combineLatest([this.formValues$.value$, this.exportColumns.asObservable()]).pipe(
			untilDestroyed(this),
			map(([values, exportColumns]) => exportColumns.filter((column) => values.columns[column]))
		)
	);
	readonly selectedSummary$ = this.formValues$.asObservable().pipe(
		startWith(this.formValue),
		distinctUntilChangedJson((s) => s.exportType),
		map((x) => {
			const exportType = this.getExportTypeByName(x.exportType);
			return exportType?.columnSet === "summary";
		})
	);
	readonly selectedSummaryLike$ = this.formValues$.asObservable().pipe(
		startWith(this.formValue),
		distinctUntilChangedJson((s) => s.exportType),
		map((x) => {
			const exportType = this.getExportTypeByName(x.exportType);
			return exportType?.columnSet === "summary" || exportType?.view === "project_summary_user";
		})
	);
	readonly selectedProjectSummaryUser$ = this.formValues$.asObservable().pipe(
		startWith(this.formValue),
		distinctUntilChangedJson((s) => s.exportType),
		map((x) => {
			const exportType = this.getExportTypeByName(x.exportType);
			return exportType?.view === "project_summary_user";
		})
	);
	constructor(
		private timesService: TimesService,
		@Inject(MAT_DIALOG_DATA)
		private data: {
			filter: ReportExportFilter;
		},
		private ref: MatDialogRef<ReportsExportDialogComponent>,
		private translateService: TranslateService,
		private dialog: MatDialog,
		private appSettingsQuery: ApplicationSettingsQuery,
		private appService: AppService,
		private userSettingsQuery: UserSettingsQuery,
		private reportsService: ReportsService
	) {}
	private setSubmittingState(isSubmitting: boolean) {
		this.isLoading = isSubmitting;
		this.ref.disableClose = isSubmitting;
		if (isSubmitting) this.group.disable();
		else this.group.enable();
	}
	private handleSubmitError(error: unknown) {
		log.error("Export failed", error);
		this.setSubmittingState(false);
	}
	private completeSuccessfulExport() {
		this.reportsService.updateDashboardColums();
		this.ref.close();
	}
	close() {
		if (!this.closeDisabled) this.ref.close();
	}
	ngOnInit(): void {
		this.ref.updateSize("700px");
		const initialValue = this.formValue;
		if (!this.hasExportTypeForFileType(initialValue.fileType)) {
			const fallbackFileType = this.visibleFileTypeOptions[0]?.value ?? "excel";
			this.group.patchValue({ fileType: fallbackFileType }, { emitEvent: false });
		}
		const valueAfterFileTypeFallback = this.formValue;
		const initialExportType = this.getValidExportTypeNameByFileType(
			valueAfterFileTypeFallback.fileType,
			valueAfterFileTypeFallback.exportType
		);
		if (initialExportType !== valueAfterFileTypeFallback.exportType) {
			this.group.patchValue({ exportType: initialExportType }, { emitEvent: false });
		}
		this.applyColumnsForExportType(this.getExportTypeByName(this.formValue.exportType), true);
		// this.group.valueChanges
		// 	.pipe(
		// 		untilDestroyed(this),
		// 		distinctUntilChangedJson((s) => s.rounding)
		// 	)
		// 	.pipe(
		// 		switchMap(async (value) => {
		// 			if (value.rounding) {
		// 				await this.appService.updateRounding(value.rounding);
		// 			}
		// 		})
		// 	)
		// 	.subscribe();
		this.group.controls.fileType.valueChanges
			.pipe(untilDestroyed(this), distinctUntilChanged())
			.subscribe((fileType) => {
				if (!fileType) {
					return;
				}
				const currentExportType = this.group.controls.exportType.value;
				const nextExportType = this.getValidExportTypeNameByFileType(fileType, currentExportType);
				if (nextExportType !== currentExportType) {
					this.group.patchValue({ exportType: nextExportType }, { emitEvent: true });
				}
			});
		this.group.controls.exportType.valueChanges
			.pipe(untilDestroyed(this), distinctUntilChanged())
			.subscribe((exportTypeName) => {
				this.selectAllValidColumnsForExportType(this.getExportTypeByName(exportTypeName));
				this.group.patchValue({ columnType: 0 }, { emitEvent: false });
			});
	}
	getErrorObservable(controlName: string, langName: string) {
		return useFormErrorObservable(this)(
			controlName,
			() => this.group.controls[controlName],
			{
				required: (error, ctrl) => ({
					content: "errors.required",
					args: { field: langName },
				}),
			},
			(key) => this.translateService.instant(langName ?? key)
		);
	}
	get config(): ReportsExportsConfig {
		return this.appSettingsQuery.getValue().reports?.export;
	}
	set config(val: ReportsExportsConfig) {
		this.appSettingsQuery.updateByKey("reports.export", val);
	}
	progress = createRxValue<ExportProgress>(null);
	get closeDisabled() {
		return this.ref.disableClose;
	}
	private getSelectedExportType(name: string): ExportTypeOption | null {
		const selectedExportType = this.getExportTypeByName(name);
		if (selectedExportType) return selectedExportType;
		const fallbackExportType = this.availableExportTypes.find(
			(type): type is ExportTypeOption => !(type as ExportTypeHeader).type
		);
		return fallbackExportType ?? null;
	}
	private persistExportColumnsConfig(exportType: ExportTypeOption, showColumns: ExportColumn[] | null) {
		if (!exportType.columnSet || !showColumns) {
			return;
		}
		this.config = {
			...this.config,
			columns: showColumns,
			...(exportType.columnSet === "projecthours"
				? { projecthoursColumns: showColumns as ProjecthoursExportColumn[] }
				: { summaryColumns: showColumns as SummaryExportColumn[] }),
		} as ReportsExportsConfig;
	}
	private buildProjectHoursExportPayload(
		value: ReportsExportFormValue,
		exportType: ExportTypeOption,
		showColumns: ExportColumn[],
		filter: ReportExportFilter,
		userId: string
	) {
		const mapTimezoneToApiValue = (timezone: string): ApiTimezone => (timezone === "adjust" ? "local" : "keep");
		const selectedProjects = filter.projects?.length ? filter.projects : undefined;
		const projectStatus = (filter.projectStatus as "completed" | "not_completed" | "both" | undefined) ?? undefined;
		const includeCompleted = !selectedProjects || this.includesCompletedProjects(filter);
		return {
			projectSummary: {
				budgetFilter: "all",
				includeCompleted,
				metric: value.projectSummaryUserMetric,
			},
			projectSummaryUser: {
				metric: value.projectSummaryUserMetric,
				projectAlias: value.projectSummaryUserProjectAlias,
			},
			ampm: this.appService.isAMPM,
			locale: this.translateService.currentLang,
			type: exportType.pdf ? "pdf" : exportType.csv ? "csv" : "excel",
			view: exportType.view,
			clients: filter.clients?.length ? filter.clients : undefined,
			projects: selectedProjects ?? "all_with_archived",
			tasks: filter.tasks?.length ? filter.tasks : undefined,
			tags: filter.tags?.length ? filter.tags : undefined,
			projectTags: filter.projectTags?.length ? filter.projectTags : undefined,
			billable: filter.billable ? filter.billable : undefined,
			completed: selectedProjects ? projectStatus : "both",
			users: filter.users?.length ? filter.users : [userId],
			range: {
				start: filter.startDate.toISOString(),
				end: filter.endDate.toISOString(),
			} as any,
			rounding: value.rounding?.enabled
				? {
						minutes: value.rounding.minutes,
						type: value.rounding.type ?? RoundingTypes.UpTo,
					}
				: undefined,
			timezone: mapTimezoneToApiValue(value.timezone),
			columns: showColumns,
		} as ExportProjectHoursSchema;
	}
	private async submitProjectHoursExport(
		value: ReportsExportFormValue,
		exportType: ExportTypeOption,
		filter: ReportExportFilter,
		userId: string
	) {
		const showColumns = this.getFilteredActiveColumns(value);
		this.persistExportColumnsConfig(exportType, showColumns);
		try {
			const { blob, fileName } = await this.exportProjectHours(
				this.buildProjectHoursExportPayload(value, exportType, showColumns, filter, userId)
			);
			log.debug(blob.type);
			this.progress.value = { finished: true, loaded: blob.size, percent: 1, total: blob.size };
			saveAs(blob, fileName);
			this.completeSuccessfulExport();
		} catch (error) {
			this.handleSubmitError(error);
		}
	}
	private async exportProjectHours(data: ExportProjectHoursSchema) {
		try {
			const apiUrl = "/export_projecthours";
			const body = exportProjectHoursSchema.parse(data);
			const response = (await firstValueFrom(
				this.timesService.adapter.post(apiUrl, body, {
					responseType: "blob" as "json",
					observe: "response",
					headers: {
						"Content-Type": "application/json",
					},
				})
			)) as HttpResponse<Blob>;
			if (!response.body) {
				throw new Error("Export response body is empty");
			}

			this.progress.value = {
				finished: true,
				loaded: response.body.size,
				percent: 1,
				total: response.body.size,
			};
			const ext = {
				csv: "csv",
				excel: "xlsx",
				pdf: "pdf",
			}[data.type];
			if (!ext) {
				throw new Error(`Invalid file extension for type ${data.type}`);
			}
			const headerFileName = extractFilenameFromExportHeaders(response.headers);
			const fallbackFileName = `timeghost_projecthours_export_${new Date().toLocaleString().replace(/:/g, "-")}.${ext}`;
			return {
				blob: response.body,
				fileName: headerFileName ?? fallbackFileName,
				ext,
			} as const;
		} catch (error) {
			log.error("Error exporting project hours:", error);
			throw error;
		} finally {
			this.progress.value = null;
			this.setSubmittingState(false);
		}
	}
	onColumnChange(column: ExportColumn, checked: boolean) {
		this.group.patchValue({ columns: { ...this.formValue.columns, [column]: checked } }, { emitEvent: true });
	}

	async submit() {
		const value = this.formValue;
		this.group.updateValueAndValidity();
		if (this.group.invalid) return;
		this.setSubmittingState(true);
		const filter = this.data.filter;
		if (!filter.users.length && this.reportsService) {
			const usersInDataset = this.reportsService.usersInDataset.value;
			if (usersInDataset.length) {
				filter.users = usersInDataset;
			}
		}
		const currentUserSettings = this.userSettingsQuery.getValue();

		const exportType = this.getSelectedExportType(value.exportType);
		if (!exportType) {
			this.setSubmittingState(false);
			return;
		}
		await this.submitProjectHoursExport(value, exportType, filter as ReportExportFilter, currentUserSettings.id);
	}
	async onRoundingHandle(forceUpdate?: boolean) {
		const { rounding } = this.group.getRawValue();
		if (rounding?.enabled && !forceUpdate) {
			this.group.patchValue({
				rounding: { ...rounding, enabled: false },
			});
			return;
		}
		const newConfig = await firstValueFrom(
			this.dialog
				.open(RoundingDialogComponent, {
					data: new RoundingConfig({
						data: rounding ? { ...rounding, enabled: true } : null,
						hideToggle: true,
					}),
				})
				.afterClosed()
		);
		if (newConfig) {
			this.group.patchValue({
				rounding: { ...newConfig },
			});
		}
	}
}
